'use client';

import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { ScrollReveal } from '@/components/ScrollReveal';
import HeroVisual from '@/components/HeroVisual';
import {
  FileText, ClipboardList, Zap, Rocket, Check, X, Archive, Sparkles,
  Mic, Briefcase, MessageSquare, Wand2, Landmark, Layout, AudioLines,
  BookOpen, SearchCheck, MapPin, Search, Mail, Camera, Scale, Clock,
  ShieldCheck, Calculator, FileSearch, AlertTriangle, CheckCircle2,
  Forward, Chrome,
} from 'lucide-react';

export default function HomeContent() {
  return (
    <div className="min-h-screen w-full flex flex-col items-center">
      {/* Hero — full-bleed, no card wrapper */}
      <section className="hero-gradient w-full py-24 md:py-32 px-6 relative overflow-hidden">
        <img src="/images/hero.png" alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.10]" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/50" />
        <div className="relative max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-16">
          {/* Left — copy */}
          <div className="flex-1 flex flex-col items-center lg:items-start text-center lg:text-left">
            <motion.h1
              className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-gray-900 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1.0] }}
            >
              Superpower{' '}
              <span className="bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">
                Your Resume
              </span>
            </motion.h1>
            <motion.p
              className="text-xl md:text-2xl text-gray-600 mb-10 max-w-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.12, ease: [0.25, 0.1, 0.25, 1.0] }}
            >
              Instantly tailor your resume to any job description and get more interviews — powered by AI that verifies every claim against your real career history.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.24, ease: [0.25, 0.1, 0.25, 1.0] }}
            >
              <motion.a
                href="https://app.superpowerresume.com/sign-up"
                className="inline-block bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold py-4 px-10 rounded-xl shadow-lg text-lg"
                whileHover={{ scale: 1.03, boxShadow: '0 0 28px rgba(37, 99, 235, 0.35)' }}
                whileTap={{ scale: 0.97 }}
              >
                Try Free — No Credit Card
              </motion.a>
              <motion.a
                href="/features"
                className="inline-block border-2 border-gray-300 text-gray-700 font-bold py-4 px-10 rounded-xl text-lg hover:border-blue-400 hover:text-blue-600 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
              >
                See How It Works
              </motion.a>
            </motion.div>
            <motion.div
              className="flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-sm font-medium text-gray-600"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {[
                'Free plan — no credit card',
                'ATS-optimized output',
                'First resume in minutes',
              ].map((label) => (
                <span key={label} className="flex items-center gap-1.5">
                  <Check className="w-4 h-4 text-green-600" strokeWidth={3} />
                  {label}
                </span>
              ))}
            </motion.div>
          </div>

          {/* Right — product visual */}
          <div className="flex-1 flex justify-center lg:justify-end">
            <HeroVisual />
          </div>
        </div>
      </section>

      <main className="w-full max-w-5xl mx-auto flex flex-col items-center px-6">
        {/* How It Works */}
        <section className="w-full flex flex-col items-center py-24">
          <ScrollReveal>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-16 text-center text-gray-900 tracking-tight">
              How It Works
            </h2>
          </ScrollReveal>
          <div className="flex flex-col md:flex-row justify-center gap-10 w-full">
            {[
              { icon: FileText, step: 1, title: 'Upload Your Career History', desc: 'Upload every resume you\'ve ever had — plus your LinkedIn profile. The more history you add, the smarter the results.' },
              { icon: ClipboardList, step: 2, title: 'Paste the Job', desc: 'Paste the job description or URL for the role you want to target.' },
              { icon: Zap, step: 3, title: 'Get Your Results', desc: 'Receive a tailored resume, ATS match score, and cover letter — all personalized from your career history.' },
            ].map(({ icon: Icon, step, title, desc }, i) => (
              <ScrollReveal key={step} delay={i * 0.12} className="flex-1">
                <div className="flex flex-col items-center bg-white rounded-2xl border border-gray-100 shadow-lg p-10 relative group hover:shadow-2xl transition-shadow">
                  <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-blue-500 text-white text-sm font-bold px-4 py-1 rounded-full shadow">
                    Step {step}
                  </span>
                  <div className="w-14 h-14 rounded-2xl bg-blue-600 flex items-center justify-center mb-6 shadow-md">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-extrabold mb-3 text-gray-900 text-center tracking-tight">
                    {title}
                  </h3>
                  <p className="text-gray-600 text-center text-lg">{desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </section>

        {/* Truth & verification — key differentiator */}
        <ScrollReveal className="w-full mb-24">
          <section className="bg-white rounded-2xl shadow-xl border border-gray-100 px-8 py-16 md:py-20">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
                <ShieldCheck className="w-4 h-4" />
                Verified Against Your Real History
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-gray-900 tracking-tight">
                The AI Resume Builder That Can&apos;t Make Things Up
              </h2>
              <p className="text-lg text-gray-500 max-w-2xl mx-auto">
                Generic AI invents impressive-sounding bullets. Ours is structurally
                prevented from it — every resume is built from, and checked against,
                your real career record.
              </p>
            </div>

            <div className="flex flex-col lg:flex-row items-center gap-12">
              {/* Left — the three guarantees */}
              <div className="flex-1 space-y-8">
                {[
                  {
                    icon: Calculator,
                    title: 'Numbers only from your real history',
                    desc: 'The generation pipeline cannot include a metric that isn’t in your source career data. Fabricated numbers are structurally impossible — anything unsupported is stripped and becomes a fill-in prompt for you.',
                  },
                  {
                    icon: FileSearch,
                    title: 'Every claim checked after generation',
                    desc: 'Each bullet is automatically verified against your uploaded documents and career timeline, then labeled verified, unverified, or conflicting — so you know exactly what’s backed up before you hit send.',
                  },
                  {
                    icon: ShieldCheck,
                    title: 'Provenance on every bullet',
                    desc: 'Hover any verified claim to see where it came from — “Traced to: Director of Technology at AutoAnything.” Walk into the interview knowing you can defend every line.',
                  },
                ].map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center shadow-md flex-shrink-0">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-extrabold text-gray-900 mb-1 tracking-tight">{title}</h3>
                      <p className="text-gray-600 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Right — coded mock of verified bullets with provenance */}
              <div className="flex-1 w-full max-w-lg">
                <div className="bg-gray-50 rounded-2xl border border-gray-200 shadow-lg p-6">
                  <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                    <div>
                      <p className="font-extrabold text-gray-900">Director of Technology</p>
                      <p className="text-sm text-gray-500">AutoAnything</p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full">
                      <ShieldCheck className="w-3.5 h-3.5" />
                      Claims checked
                    </span>
                  </div>
                  <ul className="space-y-3">
                    {/* Verified bullet with visible provenance tooltip */}
                    <li className="relative group">
                      <div className="flex items-start gap-3 bg-white rounded-xl border border-green-200 p-4">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-gray-800 leading-relaxed">
                          Cut infrastructure spend <b>38%</b> by consolidating three data centers onto a single cloud platform.
                        </p>
                      </div>
                      <div className="absolute -top-3 right-4 translate-y-[-100%] z-10 hidden group-hover:block">
                        <div className="bg-gray-900 text-white text-xs font-medium px-3 py-2 rounded-lg shadow-xl whitespace-nowrap">
                          Traced to: Director of Technology at AutoAnything
                          <span className="absolute left-6 -bottom-1 w-2 h-2 bg-gray-900 rotate-45" />
                        </div>
                      </div>
                      <p className="flex items-center gap-1.5 text-xs text-green-700 font-medium mt-1.5 ml-1">
                        <Check className="w-3.5 h-3.5" strokeWidth={3} />
                        Verified &middot; Traced to: Director of Technology at AutoAnything
                      </p>
                    </li>
                    {/* Second verified bullet — tooltip on hover */}
                    <li className="relative group">
                      <div className="flex items-start gap-3 bg-white rounded-xl border border-green-200 p-4">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-gray-800 leading-relaxed">
                          Led a <b>14-person</b> engineering team across platform, data, and DevOps functions.
                        </p>
                      </div>
                      <div className="absolute -top-3 right-4 translate-y-[-100%] z-10 hidden group-hover:block">
                        <div className="bg-gray-900 text-white text-xs font-medium px-3 py-2 rounded-lg shadow-xl whitespace-nowrap">
                          Traced to: Director of Technology at AutoAnything
                          <span className="absolute left-6 -bottom-1 w-2 h-2 bg-gray-900 rotate-45" />
                        </div>
                      </div>
                    </li>
                    {/* Unverified bullet — amber state */}
                    <li>
                      <div className="flex items-start gap-3 bg-amber-50 rounded-xl border border-amber-200 p-4">
                        <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-gray-800 leading-relaxed">
                            Reduced customer churn by improving onboarding flows.
                          </p>
                          <p className="text-xs text-amber-700 font-semibold mt-1.5">
                            Couldn&apos;t verify — edit or confirm
                          </p>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
                <p className="text-center text-sm text-gray-400 mt-4">
                  Claim verification is included on every plan — Free through Executive.
                </p>
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* Everything You Need to Land the Job */}
        <section className="w-full mb-24">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-center text-gray-900 tracking-tight">
              Everything You Need to Land the Job
            </h2>
            <p className="text-lg text-gray-500 mb-12 text-center max-w-2xl mx-auto">
              More than a resume builder — a complete job search platform.
            </p>
          </ScrollReveal>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {[
              {
                icon: FileText,
                title: 'Resumes & Cover Letters',
                color: 'from-blue-600 to-blue-500',
                items: [
                  { icon: Sparkles, text: 'AI-tailored to any job description' },
                  { icon: FileText, text: 'Matching cover letters in one click' },
                  { icon: Wand2, text: 'One-click auto-improve for ATS gaps' },
                  { icon: MessageSquare, text: 'AI chat to refine any section' },
                  { icon: Landmark, text: 'Federal/government resume support' },
                  { icon: Layout, text: '3 professional templates' },
                ],
              },
              {
                icon: Mic,
                title: 'Interview Preparation',
                color: 'from-violet-600 to-violet-500',
                items: [
                  { icon: AudioLines, text: 'Voice mock interviews with 3D talking avatars' },
                  { icon: MessageSquare, text: '6 round types including panel interviews' },
                  { icon: BookOpen, text: 'Company-specific coaching for 40+ employers' },
                  { icon: SearchCheck, text: 'Real-time scoring + skills gap analysis' },
                ],
              },
              {
                icon: Briefcase,
                title: 'Job Search & Career Tools',
                color: 'from-emerald-600 to-emerald-500',
                items: [
                  { icon: Forward, text: 'Apply by Email — forward a job posting, get a tailored resume back' },
                  { icon: Chrome, text: 'Chrome extension — generate resumes right on LinkedIn and Indeed' },
                  { icon: MapPin, text: 'Application tracking with smart follow-ups' },
                  { icon: Search, text: 'LinkedIn optimization suite with AI coaching' },
                  { icon: Mail, text: '18 email templates (follow-up, thank-you, networking)' },
                  { icon: Camera, text: 'AI headshot enhancement' },
                  { icon: Scale, text: 'Offer comparison tool' },
                  { icon: Clock, text: 'Federal resume mode with KSA statements' },
                ],
              },
            ].map(({ icon: Icon, title, color, items }, i) => (
              <ScrollReveal key={title} delay={i * 0.1}>
                <div className="bg-white rounded-2xl border border-gray-100 shadow-lg hover:shadow-2xl transition-shadow h-full flex flex-col">
                  <div className={`bg-gradient-to-r ${color} rounded-t-2xl px-6 py-5 flex items-center gap-3`}>
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white">{title}</h3>
                  </div>
                  <ul className="px-6 py-5 space-y-3 flex-1">
                    {items.map(({ icon: ItemIcon, text }) => (
                      <li key={text} className="flex items-start gap-3">
                        <ItemIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <div className="text-center">
            <Link href="/features" className="inline-block bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-bold py-3 px-8 rounded-xl shadow text-lg transition-colors">
              See Full Feature Breakdown
            </Link>
          </div>
        </section>

        {/* See It In Action — product screenshots */}
        <section className="w-full mb-24">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-center text-gray-900 tracking-tight">
              See It In Action
            </h2>
            <p className="text-lg text-gray-500 mb-16 text-center max-w-2xl mx-auto">
              A real look inside the platform — from your dashboard to interview prep.
            </p>
          </ScrollReveal>

          {/* Alternating feature rows */}
          <div className="space-y-20">
            {[
              {
                img: '/images/screenshots/dashboard.png',
                alt: 'Dashboard showing usage tracking, job pipeline, and application stats',
                title: 'Your Job Search Command Center',
                desc: 'Track every application, monitor your usage, and see your entire job pipeline at a glance. Smart action items surface what needs your attention — overdue follow-ups, stale applications, and upcoming interviews.',
                reverse: false,
              },
              {
                img: '/images/screenshots/mock-interview.png',
                alt: 'Mock interview round selection with 6 interview types and 3D avatars',
                title: 'Practice Every Interview Round',
                desc: 'Choose from recruiter screens, behavioral, technical, hiring manager, panel, and executive rounds. Each session features a 3D talking avatar, is tailored to your target job and company, and scored with real-time feedback.',
                reverse: true,
              },
              {
                img: '/images/screenshots/email-templates.png',
                alt: 'Email template editor with AI personalization',
                title: 'Professional Emails in Seconds',
                desc: 'Pick a template, fill in the details, and hit "AI Personalize" — get a polished, job-specific email ready to send. Covers every stage from post-application follow-ups to offer negotiations.',
                reverse: false,
              },
              {
                img: '/images/screenshots/job-tracker.png',
                alt: 'Job tracker Kanban board with Saved, Applied, Interviewing, and Offer columns',
                title: 'Track Every Application',
                desc: 'A drag-and-drop Kanban board to manage your entire pipeline. Each job card links to its resume, cover letter, and follow-up emails — so nothing falls through the cracks.',
                reverse: true,
              },
            ].map(({ img, alt, title, desc, reverse }, i) => (
              <ScrollReveal key={title} delay={0.1}>
                <div className={`flex flex-col ${reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-10 lg:gap-16`}>
                  <div className="flex-1 w-full">
                    <img
                      src={img}
                      alt={alt}
                      className="rounded-2xl shadow-2xl border border-gray-200 w-full"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex-1 flex flex-col justify-center">
                    <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4 tracking-tight">
                      {title}
                    </h3>
                    <p className="text-lg text-gray-600 leading-relaxed">{desc}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>

          {/* Smaller feature gallery */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-20 max-w-3xl mx-auto">
            {[
              {
                img: '/images/screenshots/career-interview.png',
                alt: 'Career profile interview with AI asking about experience',
                title: 'AI Career Interview',
                desc: 'Build your career profile through a guided conversation — the AI asks smart follow-ups to capture your full story.',
              },
              {
                img: '/images/screenshots/headshots.png',
                alt: 'AI headshot enhancer with upload and enhanced photo',
                title: 'AI Headshot Enhancer',
                desc: 'Upload any photo and get a professional headshot with studio-quality lighting and a clean background.',
              },
            ].map(({ img, alt, title, desc }, i) => (
              <ScrollReveal key={title} delay={i * 0.1}>
                <div className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden hover:shadow-2xl transition-shadow">
                  <img
                    src={img}
                    alt={alt}
                    className="w-full border-b border-gray-100"
                    loading="lazy"
                  />
                  <div className="p-5">
                    <h4 className="font-bold text-gray-900 mb-2">{title}</h4>
                    <p className="text-sm text-gray-600">{desc}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </section>

        {/* Apply from anywhere — Apply by Email + Chrome extension */}
        <section className="w-full mb-24">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-center text-gray-900 tracking-tight">
              Apply From Anywhere
            </h2>
            <p className="text-lg text-gray-500 mb-12 text-center max-w-2xl mx-auto">
              You don&apos;t even have to open the app. Tailored resumes come to you —
              in your inbox and right on the job board.
            </p>
          </ScrollReveal>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Apply by Email — prominent card */}
            <ScrollReveal className="lg:col-span-3">
              <div className="bg-gradient-to-br from-blue-600 to-blue-500 rounded-2xl shadow-xl p-8 md:p-10 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <Forward className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-extrabold text-white tracking-tight">Apply by Email</h3>
                </div>
                <p className="text-blue-100 text-lg mb-6 leading-relaxed">
                  Forward any job posting to{' '}
                  <span className="font-bold text-white whitespace-nowrap">apply@superpowerresume.com</span>{' '}
                  — a tailored resume comes back. Recruiter email, job alert, a posting a
                  friend sent you: forward it and keep moving.
                </p>
                {/* Coded inbox mock */}
                <div className="bg-white rounded-xl shadow-lg p-4 mt-auto">
                  <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">Fwd: Senior Product Manager — Acme Corp</p>
                      <p className="text-xs text-gray-500">To: apply@superpowerresume.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 pt-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">Your tailored resume is ready</p>
                      <p className="text-xs text-gray-500">From: Superpower Resume &middot; resume + ATS match score attached</p>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            {/* Chrome extension card */}
            <ScrollReveal delay={0.1} className="lg:col-span-2">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-lg hover:shadow-2xl transition-shadow p-8 md:p-10 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center shadow-md">
                    <Chrome className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-extrabold text-gray-900 tracking-tight">Chrome Extension</h3>
                </div>
                <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                  Generate resumes right on LinkedIn and Indeed. Import jobs with one
                  click, auto-fill applications across 16 ATS platforms, and never
                  switch tabs.
                </p>
                <Link
                  href="/chrome-extension"
                  className="mt-auto inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 font-semibold transition-colors"
                >
                  Explore the extension &rarr;
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Career Vault — key differentiator */}
        <ScrollReveal className="w-full mb-24">
          <section className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl shadow-2xl px-8 py-16 md:py-20 border border-gray-700">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(59,130,246,0.15),_transparent_50%)]" />
            <div className="relative z-10 max-w-3xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center shadow-md">
                  <Archive className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">
                  Your Entire Career, Always Ready
                </h2>
              </div>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                Most people only keep one resume — a highlight reel of their last few roles. But what about that niche skill from 10 years ago that one job happens to need?
              </p>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-white font-semibold mb-2">Here&apos;s the magic</p>
                    <p className="text-gray-300 leading-relaxed">
                      Upload every resume you&apos;ve ever had — going back 5, 10, even 20 years. When you target a new job, our AI scans your <em className="text-blue-400 not-italic font-medium">entire history</em> and automatically surfaces the experience that matters most. That obscure certification, that one project with a niche technology, that role nobody else applying can claim — it all becomes ammunition for your next application.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                {['Old resumes', 'LinkedIn profile', 'Past cover letters', 'Performance reviews'].map((item) => (
                  <span key={item} className="inline-flex items-center gap-1.5 bg-white/10 text-gray-300 text-sm font-medium px-4 py-2 rounded-full border border-white/10">
                    <Check className="w-3.5 h-3.5 text-blue-400" />
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* Built for Every Stage */}
        <ScrollReveal className="w-full mb-24">
          <section className="relative flex flex-col items-center">
            <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-blue-700 via-blue-500 to-blue-400 opacity-20 rounded-3xl blur-sm z-0" />
            <div className="relative z-10 bg-white/95 rounded-3xl shadow-xl border border-blue-100 px-8 py-20 flex flex-col items-center w-full">
              <div className="flex flex-col items-center mb-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-blue-400 flex items-center justify-center shadow-lg border-4 border-white">
                  <Rocket className="w-8 h-8 text-white" />
                </div>
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold mb-6 text-center bg-gradient-to-r from-blue-700 via-blue-500 to-blue-400 bg-clip-text text-transparent tracking-tight">
                Built for Every Stage of Your Career
              </h2>
              <p className="text-xl md:text-2xl font-semibold mb-10 text-center text-gray-600 max-w-2xl mx-auto">
                Whether you&apos;re landing your first role, switching industries, or going after a promotion — we&apos;ve got you.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-4xl mx-auto">
                {[
                  { label: 'New Graduates', desc: 'Turn internships and coursework into compelling bullets.' },
                  { label: 'Career Changers', desc: 'Reframe your experience for a completely new industry.' },
                  { label: 'Mid-Career Pros', desc: 'Tailor a decade of experience to the exact role you want.' },
                  { label: 'Returning to Work', desc: 'Highlight what matters and close career gaps with confidence.' },
                  { label: 'Federal & Government', desc: 'Build USAJobs-ready resumes with KSA statements and specialized experience.' },
                  { label: 'Freelancers & Consultants', desc: 'Showcase project-based work with umbrella entries and client breakdowns.' },
                ].map(({ label, desc }) => (
                  <div key={label} className="bg-gradient-to-br from-blue-50 via-white to-gray-50 border border-blue-100 rounded-xl px-6 py-4 text-center">
                    <p className="font-bold text-gray-900 mb-1">{label}</p>
                    <p className="text-sm text-gray-600">{desc}</p>
                  </div>
                ))}
                <Link
                  href="/executive"
                  className="md:col-span-2 lg:col-span-3 bg-gray-900 border border-gray-700 rounded-xl px-6 py-4 text-center hover:bg-gray-800 transition-colors group"
                >
                  <p className="font-bold text-white mb-1">Executives &amp; Senior Leaders</p>
                  <p className="text-sm text-gray-300">
                    Position for the C-suite with value profiles, board-ready documents, and role playbooks.{' '}
                    <span className="text-blue-400 font-semibold group-hover:text-blue-300 whitespace-nowrap">Explore Executive &rarr;</span>
                  </p>
                </Link>
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* Why Not Just Use ChatGPT? */}
        <ScrollReveal className="w-full mb-24">
          <section className="bg-white rounded-2xl shadow-xl px-8 py-16 flex flex-col items-center border border-gray-100">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-center text-gray-900 tracking-tight">
              Why Not Just Use ChatGPT?
            </h2>
            <p className="text-lg text-gray-500 mb-10 text-center max-w-2xl">
              You could — but here&apos;s what you&apos;d miss.
            </p>
            <div className="w-full max-w-3xl">
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-6 py-4 font-bold text-gray-800"></th>
                      <th className="px-6 py-4 font-bold text-gray-500 text-center">ChatGPT / Templates</th>
                      <th className="px-6 py-4 font-bold text-blue-700 text-center">Superpower Resume</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[
                      'Verifies every claim against your real history',
                      'Cannot invent numbers or metrics',
                      'Mines decades of career history',
                      'Tailors to specific job descriptions',
                      'ATS match scoring',
                      'Keyword gap analysis',
                      'Matching cover letter',
                      'Mock interview practice (text & voice)',
                      'Interview prep guides with talking points',
                      'Follow-up reminders & email generation',
                      'Resume quality scoring',
                      'Federal/government resume support',
                      'Stores your documents securely',
                      'Sounds like you (not generic AI)',
                      'Job tracking built in',
                    ].map((feature, idx) => (
                      <tr key={idx} className="hover:bg-blue-50/40">
                        <td className="px-6 py-3 font-medium text-gray-900">{feature}</td>
                        <td className="px-6 py-3 text-center">
                          <X className="w-5 h-5 text-gray-300 mx-auto" />
                        </td>
                        <td className="px-6 py-3 text-center">
                          <Check className="w-5 h-5 text-green-500 mx-auto" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="mt-8">
              <Link href="/pricing" className="text-blue-600 hover:text-blue-800 font-semibold transition-colors">
                See pricing &rarr;
              </Link>
            </div>
          </section>
        </ScrollReveal>

        {/* Newsletter CTA */}
        <ScrollReveal className="w-full mb-24">
          <section className="bg-gradient-to-r from-blue-50 to-gray-50 border border-blue-100 rounded-2xl shadow-lg px-8 py-12 flex flex-col items-center text-center">
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Free Weekly Career Tips</h2>
            <p className="text-gray-600 mb-6 max-w-lg">Resume strategies, job search advice, and AI tools — delivered every week.</p>
            <Link href="/newsletter" className="inline-block bg-gray-900 hover:bg-gray-800 text-white font-bold py-3 px-8 rounded-xl shadow text-lg transition-colors">
              Subscribe to Newsletter
            </Link>
          </section>
        </ScrollReveal>

        {/* Bottom CTA */}
        <ScrollReveal className="w-full mb-24">
          <section className="bg-gradient-to-r from-blue-600 to-blue-500 rounded-2xl shadow-2xl px-8 py-20 flex flex-col items-center">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-center text-white tracking-tight">
              Ready to land more interviews?
            </h2>
            <p className="text-lg text-blue-100 mb-8 text-center max-w-2xl">
              Create your free account and generate your first tailored resume in minutes.
            </p>
            <motion.a
              href="https://app.superpowerresume.com/sign-up"
              className="inline-block bg-white text-blue-700 font-bold py-3 px-8 rounded-xl shadow text-lg"
              whileHover={{ scale: 1.03, boxShadow: '0 4px 20px rgba(255,255,255,0.3)' }}
              whileTap={{ scale: 0.97 }}
            >
              Get Started Free
            </motion.a>
          </section>
        </ScrollReveal>
      </main>
    </div>
  );
}
